<template>
  <div class="admin-layout">
    <AdminSidebar :isOpen="isSidebarOpen" :userData="currentUser" />

    <div class="main-content" :class="{ 'content-shifted': isSidebarOpen }">
      <nav class="admin-nav">
        <button class="menu-btn" @click="toggleSidebar">
          <i class="fas fa-bars"></i>
        </button>
      </nav>

      <div class="addon-products-container">
        <div class="header">
          <h1>Quản lý dịch vụ bổ sung</h1>
          <button class="add-btn" @click="openModal(false)">
            <i class="fas fa-plus"></i> Thêm dịch vụ mới
          </button>
        </div>

        <!-- Filters Section -->
        <div class="filter-section">
          <div class="search-box">
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Tìm kiếm theo tên dịch vụ..."
              @keyup.enter="handleSearch"
            />
            <i class="fas fa-search"></i>
          </div>

          <div class="filter-group">
            <select v-model="filterStatus">
              <option value="">Tất cả trạng thái</option>
              <option value="true">Đang hoạt động</option>
              <option value="false">Ngừng hoạt động</option>
            </select>

            <button class="filter-btn" @click="applyFilters">
              <i class="fas fa-filter"></i> Lọc
            </button>

            <button class="clear-btn" @click="clearFilters">
              <i class="fas fa-times"></i> Xóa lọc
            </button>
          </div>
        </div>

        <!-- Table Section -->
        <div class="card">
          <div class="card-body">
            <div class="table-loading" v-if="isLoading">
              <div class="table-spinner"></div>
            </div>

            <div class="table-empty" v-else-if="addonProducts.length === 0">
              <i class="fas fa-box-open"></i>
              <p>Không tìm thấy dịch vụ bổ sung nào</p>
              <button class="add-btn" @click="openModal(false)">
                <i class="fas fa-plus"></i> Thêm dịch vụ mới
              </button>
            </div>

            <div class="table-responsive has-mobile-cards" v-else>
              <!-- Desktop Table -->
              <table class="addon-products-table admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Hình ảnh</th>
                    <th>Tên dịch vụ</th>
                    <th class="text-right">Giá</th>
                    <th class="text-center">Số lượng tối đa</th>
                    <th>Trạng thái</th>
                    <th>Ngày tạo</th>
                    <th class="col-actions">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="product in addonProducts" :key="product.addon_id">
                    <td class="nowrap">#{{ product.addon_id }}</td>
                    <td>
                      <div class="product-image-container">
                        <img
                          v-if="getProductMainImage(product)"
                          :src="getProductMainImage(product)"
                          :alt="product.name"
                          class="product-thumbnail"
                          @click="viewFullImage(getProductMainImage(product))"
                          title="Click để xem ảnh lớn"
                        />
                        <div v-else class="no-image-placeholder">
                          <i class="fas fa-image"></i>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span class="text-truncate" :title="product.name">{{ product.name }}</span>
                    </td>
                    <td class="text-right nowrap price-value">{{ formatCurrency(product.price) }}</td>
                    <td class="text-center">
                      <div class="stock-info">
                        <span
                          :class="['stock-badge', getStockStatus(product.max_quantity)]"
                        >
                          {{ product.max_quantity }}
                        </span>
                        <small v-if="product.max_quantity <= 5" class="low-stock-warning">
                          <i class="fas fa-exclamation-triangle"></i> Sắp hết hàng
                        </small>
                      </div>
                    </td>
                    <td>
                      <span
                        :class="[
                          'status-badge',
                          product.is_active ? 'active' : 'inactive'
                        ]"
                      >
                        {{
                          product.is_active ? 'Hoạt động' : 'Ngừng hoạt động'
                        }}
                      </span>
                    </td>
                    <td class="nowrap">{{ formatDate(product.created_at) }}</td>
                    <td class="col-actions">
                      <div class="action-buttons">
                        <!-- Nút quản lý tồn kho -->
                        <button
                          class="action-btn view-btn"
                          @click="openStockModal(product)"
                          title="Quản lý tồn kho"
                          aria-label="Quản lý tồn kho"
                        >
                          <i class="fas fa-boxes"></i>
                        </button>
                        <button
                          class="action-btn edit-btn"
                          @click="openModal(true, product)"
                          title="Chỉnh sửa"
                          aria-label="Chỉnh sửa dịch vụ"
                        >
                          <i class="fas fa-edit"></i>
                        </button>
                        <button
                          class="action-btn"
                          :class="product.is_active ? 'delete-btn' : 'view-btn'"
                          @click="toggleProductStatus(product)"
                          :title="product.is_active ? 'Vô hiệu hóa' : 'Kích hoạt'"
                          :aria-label="product.is_active ? 'Vô hiệu hóa dịch vụ' : 'Kích hoạt dịch vụ'"
                        >
                          <i
                            :class="
                              product.is_active
                                ? 'fas fa-toggle-on'
                                : 'fas fa-toggle-off'
                            "
                          ></i>
                        </button>
                        <button
                          class="action-btn delete-btn"
                          @click="confirmDelete(product)"
                          title="Xóa"
                          aria-label="Xóa dịch vụ"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Mobile Card View -->
              <div class="mobile-card-list">
                <div v-for="product in addonProducts" :key="'card-' + product.addon_id" class="mobile-card">
                  <div class="mobile-card-header">
                    <div class="mobile-card-title">
                      <span class="mobile-card-id">#{{ product.addon_id }}</span>
                      {{ product.name }}
                    </div>
                    <span :class="['status-badge', product.is_active ? 'active' : 'inactive']">
                      {{ product.is_active ? 'Hoạt động' : 'Ngừng' }}
                    </span>
                  </div>
                  <div class="mobile-card-body">
                    <div class="mobile-card-row" v-if="getProductMainImage(product)">
                      <div class="product-image-container mobile-image">
                        <img
                          :src="getProductMainImage(product)"
                          :alt="product.name"
                          class="product-thumbnail"
                          @click="viewFullImage(getProductMainImage(product))"
                        />
                      </div>
                    </div>
                    <div class="mobile-card-row">
                      <span class="mobile-card-label">Giá:</span>
                      <span class="mobile-card-value price-value">{{ formatCurrency(product.price) }}</span>
                    </div>
                    <div class="mobile-card-row">
                      <span class="mobile-card-label">Số lượng tối đa:</span>
                      <span class="mobile-card-value">
                        <span :class="['stock-badge', getStockStatus(product.max_quantity)]">
                          {{ product.max_quantity }}
                        </span>
                      </span>
                    </div>
                    <div class="mobile-card-row">
                      <span class="mobile-card-label">Ngày tạo:</span>
                      <span class="mobile-card-value">{{ formatDate(product.created_at) }}</span>
                    </div>
                  </div>
                  <div class="mobile-card-actions">
                    <button class="action-btn view-btn with-label" @click="openStockModal(product)">
                      <i class="fas fa-boxes"></i> Kho
                    </button>
                    <button class="action-btn edit-btn with-label" @click="openModal(true, product)">
                      <i class="fas fa-edit"></i> Sửa
                    </button>
                    <button 
                      class="action-btn with-label"
                      :class="product.is_active ? 'delete-btn' : 'view-btn'"
                      @click="toggleProductStatus(product)"
                    >
                      <i :class="product.is_active ? 'fas fa-toggle-on' : 'fas fa-toggle-off'"></i>
                      {{ product.is_active ? 'Tắt' : 'Bật' }}
                    </button>
                    <button class="action-btn delete-btn with-label" @click="confirmDelete(product)">
                      <i class="fas fa-trash"></i> Xóa
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div class="pagination-container" v-if="!isLoading && addonProducts.length > 0">
            <div class="pagination-info">
              Hiển thị {{ addonProducts.length }} dịch vụ
            </div>
            <div class="pagination-controls">
              <button
                class="pagination-btn"
                @click="changePage(currentPage - 1)"
                :disabled="currentPage === 1"
              >
                <i class="fas fa-chevron-left"></i> Trước
              </button>

              <span class="page-info">
                Trang {{ currentPage }} / {{ totalPages || 1 }}
              </span>

              <button
                class="pagination-btn"
                @click="changePage(currentPage + 1)"
                :disabled="currentPage >= totalPages"
              >
                Sau <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div class="modal" v-if="showModal" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>{{ isEditing ? 'Chỉnh sửa dịch vụ' : 'Thêm dịch vụ mới' }}</h2>
          <button class="close-btn" @click="closeModal">×</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitForm">
            <!-- Tên dịch vụ -->
            <div class="form-group">
              <label for="name"
                >Tên dịch vụ <span class="required">*</span></label
              >
              <input
                type="text"
                id="name"
                v-model="productForm.name"
                placeholder="Nhập tên dịch vụ"
                required
              />
            </div>

            <!-- Mô tả -->
            <div class="form-group">
              <label for="description">Mô tả</label>
              <textarea
                id="description"
                v-model="productForm.description"
                placeholder="Nhập mô tả về dịch vụ"
                rows="3"
              ></textarea>
            </div>

            <!-- Giá -->
            <div class="form-group">
              <label for="price">Giá <span class="required">*</span></label>
              <input
                type="number"
                id="price"
                v-model.number="productForm.price"
                placeholder="Nhập giá dịch vụ"
                min="0"
                step="1000"
                required
              />
            </div>

            <!-- Số lượng tối đa -->
            <div class="form-group">
              <label for="maxQuantity">Số lượng tối đa</label>
              <input
                type="number"
                id="maxQuantity"
                v-model.number="productForm.max_quantity"
                placeholder="Nhập số lượng tối đa có thể mua"
                min="1"
                step="1"
              />
            </div>

            <!-- Hình ảnh sản phẩm -->
            <div class="form-group">
              <label>Hình ảnh sản phẩm</label>
              
              <!-- Upload box cho nhiều ảnh -->
              <div class="multiple-image-upload">
                <div
                  v-if="!isUploading"
                  class="upload-box"
                  @click="triggerImageUpload"
                >
                  <input
                    type="file"
                    ref="imageInput"
                    @change="handleMultipleImageSelected"
                    accept="image/*"
                    multiple
                    class="image-input"
                  />
                  <i class="fas fa-cloud-upload-alt"></i>
                  <span>Chọn nhiều ảnh</span>
                  <small>PNG, JPG, GIF tối đa 2MB mỗi ảnh</small>
                </div>

                <!-- Trạng thái đang tải -->
                <div v-if="isUploading" class="upload-status">
                  <div class="upload-progress">
                    <div
                      class="upload-progress-bar"
                      :style="{ width: uploadProgress + '%' }"
                    ></div>
                  </div>
                  <span>Đang tải lên... {{ uploadProgress }}%</span>
                </div>

                <!-- Grid hiển thị ảnh đã chọn -->
                <div v-if="productForm.images && productForm.images.length > 0" class="images-grid">
                  <div
                    v-for="(image, index) in productForm.images"
                    :key="index"
                    class="image-item"
                  >
                    <img :src="image.image_url" :alt="`Image ${index + 1}`" />
                    <div class="image-controls">
                      <button
                        type="button"
                        class="primary-btn"
                        @click="setPrimaryImage(index)"
                        :class="{ active: image.is_primary }"
                        title="Đặt làm ảnh chính"
                      >
                        <i class="fas fa-star"></i>
                      </button>
                      <button
                        type="button"
                        class="remove-btn"
                        @click="removeImage(index)"
                        title="Xóa ảnh"
                      >
                        <i class="fas fa-times"></i>
                      </button>
                    </div>
                    <div class="image-order">
                      <label>Thứ tự:</label>
                      <input
                        type="number"
                        v-model.number="image.display_order"
                        min="0"
                        max="99"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Hiển thị lỗi nếu có -->
              <p v-if="uploadError" class="error-message">
                <i class="fas fa-exclamation-circle"></i> {{ uploadError }}
              </p>
            </div>

            <!-- Trạng thái -->
            <div class="form-group checkbox-group">
              <input
                type="checkbox"
                id="isActive"
                v-model="productForm.is_active"
              />
              <label for="isActive">Hoạt động</label>
              <small>{{
                productForm.is_active
                  ? 'Dịch vụ đang hoạt động'
                  : 'Dịch vụ không hoạt động'
              }}</small>
            </div>

            <!-- Nút hành động -->
            <div class="form-actions">
              <button type="button" class="cancel-btn" @click="closeModal">
                <i class="fas fa-times"></i> Hủy
              </button>
              <button type="submit" class="save-btn" :disabled="isSaving">
                <i class="fas fa-spinner fa-spin" v-if="isSaving"></i>
                <i class="fas fa-save" v-else></i>
                {{ isSaving ? 'Đang lưu...' : 'Lưu dịch vụ' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      class="modal"
      v-if="showDeleteConfirm"
      @click.self="showDeleteConfirm = false"
    >
      <div class="modal-content confirm-modal">
        <div class="modal-header">
          <h2>Xác nhận xóa</h2>
          <button class="close-btn" @click="showDeleteConfirm = false">
            ×
          </button>
        </div>
        <div class="modal-body">
          <p>
            Bạn có chắc chắn muốn xóa dịch vụ
            <strong>"{{ productToDelete?.name }}"</strong>?
          </p>
          <p class="warning">
            <i class="fas fa-exclamation-triangle"></i>
            Lưu ý: Hành động này không thể hoàn tác. Nếu dịch vụ đã được sử dụng
            trong đơn hàng, nó sẽ chỉ bị vô hiệu hóa thay vì xóa hoàn toàn.
          </p>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="showDeleteConfirm = false">
            Hủy
          </button>
          <button class="delete-btn" @click="deleteProduct">Xóa</button>
        </div>
      </div>
    </div>

    <!-- Fullsize Image Modal -->
    <div
      class="fullsize-image-modal"
      v-if="showFullImage"
      @click="showFullImage = false"
    >
      <div class="fullsize-image-container">
        <img :src="fullImageUrl" alt="Full size image" />
        <button class="close-fullimage-btn" @click="showFullImage = false">
          ×
        </button>
      </div>
    </div>

    <!-- Product Detail Modal -->
    <div class="modal" v-if="showProductDetail" @click.self="closeProductDetail">
      <div class="modal-content product-detail-modal">
        <div class="modal-header">
          <h2>{{ selectedProduct?.name }}</h2>
          <button class="close-btn" @click="closeProductDetail">×</button>
        </div>
        <div class="modal-body">
          <div class="product-detail-content">
            <div class="product-images">
              <div class="main-image">
                <img :src="selectedProductMainImage" :alt="selectedProduct?.name" />
              </div>
              <div v-if="selectedProduct?.images && selectedProduct.images.length > 1" class="image-thumbnails">
                <div
                  v-for="(image, index) in selectedProduct.images"
                  :key="index"
                  class="thumbnail"
                  :class="{ active: selectedImageIndex === index }"
                  @click="selectedImageIndex = index"
                >
                  <img :src="image.image_url" :alt="`Image ${index + 1}`" />
                </div>
              </div>
            </div>
            <div class="product-info">
              <h3>{{ selectedProduct?.name }}</h3>
              <p class="price">{{ formatCurrency(selectedProduct?.price) }}</p>
              <p class="description">{{ selectedProduct?.description }}</p>
              <div class="product-meta">
                <p><strong>Số lượng tối đa:</strong> {{ selectedProduct?.max_quantity }}</p>
                <p><strong>Trạng thái:</strong> 
                  <span :class="['status-badge', selectedProduct?.is_active ? 'active' : 'inactive']">
                    {{ selectedProduct?.is_active ? 'Hoạt động' : 'Ngừng hoạt động' }}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Stock Management Modal -->
    <div class="modal" v-if="showStockModal" @click.self="closeStockModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>Quản lý tồn kho - {{ selectedProductForStock?.name }}</h2>
          <button class="close-btn" @click="closeStockModal">×</button>
        </div>
        <div class="modal-body">
          <!-- Current Stock Info -->
          <div class="current-stock-info">
            <div class="stock-item">
              <label>Tồn kho hiện tại:</label>
              <span class="stock-value">{{ selectedProductForStock?.max_quantity }}</span>
            </div>
          </div>

          <!-- Stock Update Form -->
          <form @submit.prevent="updateStock">
            <div class="form-group">
              <label>Loại thao tác</label>
              <select v-model="stockForm.action" @change="resetStockQuantity">
                <option value="add">Nhập kho (+)</option>
                <option value="remove">Xuất kho (-)</option>
                <option value="set">Điều chỉnh tồn kho</option>
              </select>
            </div>

            <div class="form-group">
              <label>
                {{ stockForm.action === 'add' ? 'Số lượng nhập' : 
                   stockForm.action === 'remove' ? 'Số lượng xuất' : 
                   'Tồn kho mới' }}
              </label>
              <input
                type="number"
                v-model.number="stockForm.quantity"
                :min="stockForm.action === 'set' ? 0 : 1"
                :max="stockForm.action === 'remove' ? selectedProductForStock?.max_quantity : undefined"
                required
              />
            </div>

            <div class="form-group">
              <label>Lý do</label>
              <textarea
                v-model="stockForm.reason"
                placeholder="Nhập lý do thay đổi tồn kho..."
                rows="3"
              ></textarea>
            </div>

            <!-- Preview -->
            <div class="stock-preview" v-if="stockForm.quantity > 0">
              <p>Sau khi thực hiện:</p>
              <p class="preview-result">
                {{ selectedProductForStock?.max_quantity }} 
                → 
                {{ calculateNewStock() }}
              </p>
            </div>

            <div class="form-actions">
              <button type="button" class="cancel-btn" @click="closeStockModal">
                Hủy
              </button>
              <button type="submit" class="save-btn" :disabled="isUpdatingStock">
                <i class="fas fa-spinner fa-spin" v-if="isUpdatingStock"></i>
                {{ isUpdatingStock ? 'Đang cập nhật...' : 'Cập nhật tồn kho' }}
              </button>
            </div>
          </form>

          <!-- Stock History -->
          <div class="stock-history" v-if="stockHistory.length > 0">
            <h4>Lịch sử thay đổi gần đây</h4>
            <div class="history-list">
              <div 
                v-for="entry in stockHistory.slice(0, 5)" 
                :key="entry.stock_history_id"
                class="history-item"
              >
                <div class="history-info">
                  <span class="history-change" 
                        :class="entry.quantity_change > 0 ? 'positive' : 'negative'">
                    {{ entry.quantity_change > 0 ? '+' : '' }}{{ entry.quantity_change }}
                  </span>
                  <span class="history-reason">{{ entry.reason }}</span>
                </div>
                <div class="history-meta">
                  <small>{{ formatDate(entry.created_at) }}</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AdminSidebar from '../common/AdminSidebar.vue'
import axiosInstance from '@/plugins/axios'
import { ToastService } from '@/services/ToastService'
import axios from 'axios'

const isProd = process.env.VUE_APP_IS_PROD === 'true'
const API_BASE_URL = isProd
  ? process.env.VUE_APP_BACKEND_URL_PROD
  : process.env.VUE_APP_BACKEND_URL_DEV
  
export default {
  name: 'AdminAddonProducts',

  components: {
    AdminSidebar
  },

  data () {
    return {
      isSidebarOpen: false,
      addonProducts: [],
      isLoading: true,
      showModal: false,
      isEditing: false,
      isSaving: false,
      productForm: {
        name: '',
        description: '',
        price: 0,
        image_url: '',
        max_quantity: 10,
        is_active: true,
        images: [] // Thêm mảng images
      },
      searchQuery: '',
      searchTerm: '',
      filterStatus: '',
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      showDeleteConfirm: false,
      productToDelete: null,
      imagePreview: null,
      isUploading: false,
      uploadProgress: 0,
      uploadError: null,
      imageFile: null,
      showFullImage: false,
      fullImageUrl: '',
      showProductDetail: false,
      selectedProduct: null,
      selectedImageIndex: 0,
      uploadedImages: [], // Temporary storage for uploaded images

      // Stock management data
      showStockModal: false,
      selectedProductForStock: null,
      isUpdatingStock: false,
      stockForm: {
        action: 'add',
        quantity: 0,
        reason: ''
      },
      stockHistory: []
    }
  },

  computed: {
  totalPages() {
    if (!this.totalItems || this.totalItems === 0) return 1;
    return Math.ceil(this.totalItems / this.itemsPerPage);
  },

  hasFilters() {
    return this.searchTerm || this.filterStatus !== '';
  },

  selectedProductMainImage() {
    if (!this.selectedProduct) return null
      
    if (this.selectedProduct.images && this.selectedProduct.images.length > 0) {
      return this.selectedProduct.images[this.selectedImageIndex]?.image_url
    }
    
    // Fallback về image_url cũ
    return this.selectedProduct.image_url || null
  }
},

  mounted () {
    const sidebarState = localStorage.getItem('sidebarOpen')
    if (sidebarState !== null) this.isSidebarOpen = sidebarState === 'true'
    this.fetchAddonProducts()
  },

  methods: {
    toggleSidebar () {
      this.isSidebarOpen = !this.isSidebarOpen
      localStorage.setItem('sidebarOpen', this.isSidebarOpen)
    },
    async fetchAddonProducts () {
      this.isLoading = true
      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // Tính toán skip dựa trên currentPage và itemsPerPage
        const skip = (this.currentPage - 1) * this.itemsPerPage

        // Tạo params
        const params = {
          skip: skip,
          limit: this.itemsPerPage
        }

        // Thêm filters nếu có
        if (this.searchTerm) {
          params.search = this.searchTerm
        }

        if (this.filterStatus) {
          params.is_active = this.filterStatus === 'true'
        }

        const response = await axiosInstance.get(
          `${API_BASE_URL}/admin/addon-products/`,
          {
            headers,
            params
          }
        )

        this.addonProducts = response.data

        // Nếu không có dữ liệu và đang ở trang > 1, quay về trang 1
        if (this.addonProducts.length === 0 && this.currentPage > 1) {
          this.currentPage = 1
          this.fetchAddonProducts()
          return
        }

        // Ước tính tổng số items
        this.totalItems =
          response.data.length >= this.itemsPerPage
            ? this.currentPage * this.itemsPerPage + 1
            : (this.currentPage - 1) * this.itemsPerPage + response.data.length
      } catch (error) {
        console.error('Error fetching addon products:', error)

        if (error.response?.status === 401) {
          this.handleLogout()
        } else {
          ToastService.error('Không thể tải dữ liệu dịch vụ bổ sung')
        }
      } finally {
        this.isLoading = false
      }
    },

    openModal (isEditing, product = null) {
      this.isEditing = isEditing

      if (isEditing && product) {
        this.productForm = { 
          ...product,
          images: product.images || [] // Đảm bảo có mảng images
        }
        
        // Nếu không có images nhưng có image_url cũ, tạo image object
        if ((!product.images || product.images.length === 0) && product.image_url) {
          this.productForm.images = [{
            image_url: product.image_url,
            is_primary: true,
            display_order: 0
          }]
        }
      } else {
        this.productForm = {
          name: '',
          description: '',
          price: 0,
          image_url: '',
          max_quantity: 10,
          is_active: true,
          images: []
        }
      }

      this.showModal = true
    },

    closeModal () {
      this.showModal = false
      this.productForm = {
        name: '',
        description: '',
        price: 0,
        image_url: '',
        max_quantity: 10,
        is_active: true,
        images: []
      }
    },

    // Trigger file input
    triggerImageUpload() {
      this.$refs.imageInput.click()
    },

    // Xử lý khi chọn nhiều ảnh
    async handleMultipleImageSelected(e) {
      const files = Array.from(e.target.files)
      if (files.length === 0) return

      this.isUploading = true
      this.uploadProgress = 0
      this.uploadError = null

      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i]
          
          // Kiểm tra kích thước file
          if (file.size > 2 * 1024 * 1024) {
            this.uploadError = `File ${file.name} quá lớn (tối đa 2MB)`
            continue
          }

          const imageUrl = await this.uploadSingleImage(file)
          if (imageUrl) {
            this.productForm.images.push({
              image_url: imageUrl,
              is_primary: this.productForm.images.length === 0, // Ảnh đầu tiên là primary
              display_order: this.productForm.images.length
            })
          }

          // Cập nhật progress
          this.uploadProgress = Math.round(((i + 1) / files.length) * 100)
        }

        ToastService.success(`Đã tải lên ${files.length} ảnh thành công`)
      } catch (error) {
        console.error('Lỗi khi tải ảnh:', error)
        this.uploadError = 'Có lỗi xảy ra khi tải ảnh'
      } finally {
        this.isUploading = false
        // Reset input
        if (this.$refs.imageInput) {
          this.$refs.imageInput.value = ''
        }
      }
    },

    // Upload một ảnh lên imgBB
    async uploadSingleImage(file) {
      const formData = new FormData()
      formData.append('image', file)

      const apiKey = process.env.VUE_APP_IMGBB_API_KEY
      if (!apiKey) {
        throw new Error('Thiếu API key cho dịch vụ tải ảnh')
      }

      try {
        const response = await axios.post(
          'https://api.imgbb.com/1/upload',
          formData,
          {
            params: { key: apiKey }
          }
        )

        if (response.data && response.data.success) {
          return response.data.data.display_url
        } else {
          throw new Error('imgBB trả về lỗi')
        }
      } catch (error) {
        console.error('Lỗi khi tải ảnh lên imgBB:', error)
        throw error
      }
    },

    // Đặt ảnh chính
    setPrimaryImage(index) {
      this.productForm.images.forEach((img, i) => {
        img.is_primary = i === index
      })
      
      // Đồng bộ image_url với ảnh primary mới
      if (this.productForm.images[index]) {
        this.productForm.image_url = this.productForm.images[index].image_url
      }
    },

    // Xóa ảnh
    removeImage(index) {
      const removedImage = this.productForm.images[index]
      this.productForm.images.splice(index, 1)
      
      // Nếu xóa ảnh primary và còn ảnh khác
      if (this.productForm.images.length > 0) {
        const hasPrimary = this.productForm.images.some(img => img.is_primary)
        if (!hasPrimary) {
          // Đặt ảnh đầu tiên làm primary
          this.productForm.images[0].is_primary = true
          this.productForm.image_url = this.productForm.images[0].image_url
        }
      } else {
        // Nếu không còn ảnh nào, xóa image_url
        this.productForm.image_url = ''
      }
    },

    // Thêm method để lấy ảnh chính của sản phẩm
    getProductMainImage(product) {
      // Ưu tiên ảnh primary từ images array
      if (product.images && product.images.length > 0) {
        // Tìm ảnh được đánh dấu là primary
        const primaryImage = product.images.find(img => img.is_primary)
        if (primaryImage) {
          return primaryImage.image_url
        }
        // Nếu không có ảnh primary, lấy ảnh đầu tiên
        return product.images[0].image_url
      }
      // Fallback về image_url cũ nếu không có images array
      return product.image_url || null
    },

    async submitForm () {
      // Kiểm tra nếu đang tải ảnh, yêu cầu đợi
      if (this.isUploading) {
        ToastService.error('Vui lòng đợi tải ảnh lên hoàn tất')
        return
      }

      // Kiểm tra các trường bắt buộc
      if (!this.productForm.name || this.productForm.price <= 0) {
        ToastService.error('Vui lòng điền đầy đủ thông tin bắt buộc')
        return
      }

      // Nếu có lỗi upload, cảnh báo
      if (this.uploadError) {
        const continueAnyway = confirm(
          'Có lỗi khi tải ảnh lên. Bạn có muốn tiếp tục lưu mà không có ảnh không?'
        )
        if (!continueAnyway) {
          return
        }
        // Reset lỗi nếu người dùng quyết định tiếp tục
        this.uploadError = null
      }

      this.isSaving = true

      try {
        const token = localStorage.getItem('adminToken')
        if (!token) {
          throw new Error('Phiên đăng nhập hết hạn')
        }

        const headers = { Authorization: `Bearer ${token}` }

        // Tìm ảnh primary để đặt làm image_url chính
        let mainImageUrl = this.productForm.image_url || ''
        if (this.productForm.images && this.productForm.images.length > 0) {
          const primaryImage = this.productForm.images.find(img => img.is_primary)
          if (primaryImage) {
            mainImageUrl = primaryImage.image_url
          } else {
            // Nếu không có primary, lấy ảnh đầu tiên
            mainImageUrl = this.productForm.images[0].image_url
          }
        }

        // Tạo đối tượng dữ liệu để gửi lên server
        const productData = {
          name: this.productForm.name,
          description: this.productForm.description || '',
          price: Number(this.productForm.price),
          image_url: mainImageUrl, // Đồng bộ với ảnh primary
          max_quantity: Number(this.productForm.max_quantity) || 10,
          is_active: this.productForm.is_active,
          images: this.productForm.images || []
        }

        if (this.isEditing) {
          const response = await axiosInstance.put(
            `${API_BASE_URL}/admin/addon-products/${this.productForm.addon_id}`,
            productData,
            { headers }
          )
          
          // Cập nhật sản phẩm trong danh sách local
          const productIndex = this.addonProducts.findIndex(
            p => p.addon_id === this.productForm.addon_id
          )
          if (productIndex !== -1) {
            this.addonProducts[productIndex] = response.data
          }
          
          ToastService.success(`Đã cập nhật dịch vụ "${this.productForm.name}"`)
        } else {
          const response = await axiosInstance.post(
            `${API_BASE_URL}/admin/addon-products/`,
            productData,
            { headers }
          )
          
          // Thêm sản phẩm mới vào đầu danh sách
          this.addonProducts.unshift(response.data)
          
          ToastService.success(`Đã tạo dịch vụ "${this.productForm.name}"`)
        }

        this.closeModal()
        // Chỉ fetch lại nếu cần thiết (ví dụ: phân trang)
        // this.fetchAddonProducts()
      } catch (error) {
        console.error('Lỗi khi lưu dịch vụ:', error)

        let errorMessage = 'Đã xảy ra lỗi khi lưu dịch vụ'

        if (error.response) {
          errorMessage = error.response.data?.detail || 'Máy chủ từ chối yêu cầu'

          if (error.response.status === 401) {
            this.handleLogout()
            return
          }
        }

        ToastService.error(errorMessage)
      } finally {
        this.isSaving = false
      }
    },

    async toggleProductStatus (product) {
      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        const updatedStatus = !product.is_active

        await axiosInstance.put(
          `${API_BASE_URL}/admin/addon-products/${product.addon_id}`,
          {
            ...product,
            is_active: updatedStatus
          },
          { headers }
        )

        // Cập nhật state
        product.is_active = updatedStatus

        ToastService.success(
          updatedStatus
            ? `Dịch vụ "${product.name}" đã được kích hoạt`
            : `Dịch vụ "${product.name}" đã bị vô hiệu hóa`
        )
      } catch (error) {
        console.error('Error toggling product status:', error)
        ToastService.error('Không thể thay đổi trạng thái dịch vụ')
      }
    },

    confirmDelete (product) {
      this.productToDelete = product
      this.showDeleteConfirm = true
    },

    async deleteProduct () {
      if (!this.productToDelete) return

      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        await axiosInstance.delete(
          `${API_BASE_URL}/admin/addon-products/${this.productToDelete.addon_id}`,
          { headers }
        )

        // Cập nhật danh sách
        this.addonProducts = this.addonProducts.filter(
          product => product.addon_id !== this.productToDelete.addon_id
        )

        ToastService.success(`Đã xóa dịch vụ "${this.productToDelete.name}"`)
        this.showDeleteConfirm = false
        this.productToDelete = null
      } catch (error) {
        console.error('Error deleting product:', error)

        if (error.response?.status === 409) {
          ToastService.warning(
            'Dịch vụ này đã được sử dụng trong đơn hàng và đã được đánh dấu không hoạt động thay vì xóa hoàn toàn'
          )
          // Refresh để cập nhật trạng thái
          this.fetchAddonProducts()
        } else {
          ToastService.error('Không thể xóa dịch vụ này')
        }

        this.showDeleteConfirm = false
      }
    },

    handleSearch () {
      this.searchTerm = this.searchQuery.trim()
      this.currentPage = 1
      this.fetchAddonProducts()
    },

    applyFilters () {
      this.currentPage = 1
      this.fetchAddonProducts()
    },

    clearFilters () {
      this.searchQuery = ''
      this.searchTerm = ''
      this.filterStatus = ''
      this.currentPage = 1
      this.fetchAddonProducts()
    },

    changePage (newPage) {
      this.currentPage = newPage
      this.fetchAddonProducts()
    },

    formatCurrency (amount) {
      return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND'
      }).format(amount)
    },

    formatDate (date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    handleLogout () {
      localStorage.removeItem('adminToken')
      localStorage.removeItem('refreshToken')
      this.$router.push('/login')
    },

    // Mở ảnh kích thước đầy đủ
    viewFullImage (url) {
      this.fullImageUrl = url
      this.showFullImage = true
    },

    // Xử lý lỗi ảnh (ví dụ: ảnh không tồn tại)
    handleImageError (event) {
      // event.target.src = null
    },

    // Stock management methods
    getStockStatus(stock) {
      if (stock <= 0) return 'out-of-stock'
      if (stock <= 5) return 'low-stock'
      if (stock <= 10) return 'medium-stock'
      return 'good-stock'
    },

    async openStockModal(product) {
      this.selectedProductForStock = product
      this.showStockModal = true
      this.resetStockForm()
      await this.fetchStockHistory(product.addon_id)
    },

    closeStockModal() {
      this.showStockModal = false
      this.selectedProductForStock = null
      this.stockHistory = []
      this.resetStockForm()
    },

    resetStockForm() {
      this.stockForm = {
        action: 'add',
        quantity: 0,
        reason: ''
      }
    },

    resetStockQuantity() {
      this.stockForm.quantity = 0
    },

    calculateNewStock() {
      if (!this.selectedProductForStock || !this.stockForm.quantity) return this.selectedProductForStock?.max_quantity

      const current = this.selectedProductForStock.max_quantity
      
      switch (this.stockForm.action) {
        case 'add':
          return current + this.stockForm.quantity
        case 'remove':
          return Math.max(0, current - this.stockForm.quantity)
        case 'set':
          return this.stockForm.quantity
        default:
          return current
      }
    },

    async updateStock() {
      if (!this.selectedProductForStock || !this.stockForm.quantity) {
        ToastService.error('Vui lòng nhập số lượng hợp lệ')
        return
      }

      this.isUpdatingStock = true

      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        let quantityChange = 0
        switch (this.stockForm.action) {
          case 'add':
            quantityChange = this.stockForm.quantity
            break
          case 'remove':
            quantityChange = -this.stockForm.quantity
            break
          case 'set':
            quantityChange = this.stockForm.quantity - this.selectedProductForStock.max_quantity
            break
        }

        const updateData = {
          addon_id: this.selectedProductForStock.addon_id,
          quantity_change: quantityChange,
          reason: this.stockForm.reason || undefined
        }

        await axiosInstance.put(
          `${API_BASE_URL}/admin/addon-products/${this.selectedProductForStock.addon_id}/stock`,
          updateData,
          { headers }
        )

        // Cập nhật local state
        this.selectedProductForStock.max_quantity = this.calculateNewStock()
        
        // Cập nhật trong danh sách
        const productIndex = this.addonProducts.findIndex(
          p => p.addon_id === this.selectedProductForStock.addon_id
        )
        if (productIndex !== -1) {
          this.addonProducts[productIndex].max_quantity = this.selectedProductForStock.max_quantity
        }

        ToastService.success('Đã cập nhật tồn kho thành công')
        
        // Refresh stock history
        await this.fetchStockHistory(this.selectedProductForStock.addon_id)
        this.resetStockForm()

      } catch (error) {
        console.error('Lỗi khi cập nhật tồn kho:', error)
        
        let errorMessage = 'Có lỗi xảy ra khi cập nhật tồn kho'
        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail
        }
        
        ToastService.error(errorMessage)
      } finally {
        this.isUpdatingStock = false
      }
    },

    async fetchStockHistory(addonId) {
      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        const response = await axiosInstance.get(
          `${API_BASE_URL}/admin/addon-products/${addonId}/stock-history`,
          { headers }
        )

        this.stockHistory = response.data
      } catch (error) {
        console.error('Lỗi khi tải lịch sử tồn kho:', error)
      }
    }
  }
}
</script>

<style scoped>
@import '@/assets/styles/admin.scss';
@import '@/assets/styles/admin-table.scss';

.admin-layout {
  min-height: 100vh;
  background-color: #f8fafc;
  display: flex;
  position: relative;
}

.main-content {
  flex: 1;
  padding: 28px;
  transition: margin-left 0.3s;
  max-width: 100%;
  overflow-x: hidden; /* Prevent horizontal overflow */
}

.content-shifted {
  margin-left: 250px;
}

.addon-products-container {
  max-width: 100%;
  overflow-x: hidden;
}

.admin-nav {
  background-color: white;
  padding: 14px 28px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  margin-bottom: 28px;
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
}

/* Header styling */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
}

.header h1 {
  font-size: 1.8rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Button styling */
.add-btn {
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(to right, #3b82f6, #2563eb);
  color: white;
  border: none;
  box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.2);
}

.add-btn:hover {
  background: linear-gradient(to right, #2563eb, #1d4ed8);
  transform: translateY(-1px);
  box-shadow: 0 6px 8px -1px rgba(59, 130, 246, 0.3);
}

/* Filter section styling */
.filter-section {
  margin-bottom: 28px;
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  justify-content: space-between;
  align-items: center;
  background-color: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

.search-box {
  position: relative;
  width: 100%;
  max-width: 480px;
}

.search-box input {
  width: 100%;
  padding: 12px 40px 12px 46px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s;
  background-color: #f8fafc;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.03);
}

.search-box input:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  background-color: white;
}

.search-box i {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  font-size: 18px;
}

.filter-group {
  display: flex;
  gap: 14px;
  align-items: center;
}

.filter-group select {
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.95rem;
  background-color: #f8fafc;
  min-width: 180px;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.03);
  transition: all 0.2s;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  padding-right: 36px;
}

.filter-group select:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  background-color: white;
}

.filter-btn,
.clear-btn {
  padding: 12px 18px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-btn {
  background: linear-gradient(to right, #3b82f6, #2563eb);
  color: white;
  border: none;
  box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.2);
}

.filter-btn:hover {
  background: linear-gradient(to right, #2563eb, #1d4ed8);
  transform: translateY(-1px);
  box-shadow: 0 6px 8px -1px rgba(59, 130, 246, 0.3);
}

.clear-btn {
  background-color: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.clear-btn:hover {
  background-color: #e2e8f0;
}

/* Card and table styling */
.card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #f1f5f9;
  overflow: hidden;
  max-width: 100%;
}

.card-body {
  padding: 0;
  position: relative;
  min-height: 200px;
  max-width: 100%;
  overflow-x: hidden;
}

.table-responsive {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.table-responsive::-webkit-scrollbar {
  height: 8px;
}

.table-responsive::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.table-responsive::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 20px;
}

.addon-products-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background-color: white;
  font-size: 0.95rem;
}

.addon-products-table th {
  position: sticky;
  top: 0;
  background-color: #f8fafc;
  padding: 16px 20px;
  text-align: left;
  font-weight: 600;
  color: #334155;
  border-bottom: 2px solid #e2e8f0;
  z-index: 10;
}

.addon-products-table td {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.addon-products-table tbody tr {
  transition: all 0.2s ease;
}

.addon-products-table tbody tr:nth-child(even) {
  background-color: #fafafa;
}

.addon-products-table tbody tr:hover {
  background-color: #f0f9ff;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* Product image styling */
.product-image-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 64px;
  height: 64px;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f8fafc;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.product-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.product-thumbnail:hover {
  transform: scale(1.15);
  z-index: 20;
}

.no-image-placeholder {
  width: 64px;
  height: 64px;
  border-radius: 8px;
  border: 1px dashed #cbd5e0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #94a3b8;
  background-color: #f8fafc;
}

/* Status badge styling */
.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  width: fit-content;
  min-width: 100px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.status-badge.active {
  background-color: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.status-badge.inactive {
  background-color: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

/* Action buttons styling */
.actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  padding: 8px;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #475569;
  background-color: #f8fafc;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.edit-btn:hover {
  background-color: #eff6ff;
  color: #1e40af;
}

.delete-btn:hover {
  background-color: #fef2f2;
  color: #b91c1c;
}

.toggle-btn:hover {
  background-color: #f0f9ff;
  color: #0369a1;
}

/* Pagination styling */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  padding: 20px;
  border-top: 1px solid #f1f5f9;
}

.pagination-btn {
  padding: 10px 18px;
  background-color: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #334155;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.pagination-btn:hover:not(:disabled) {
  background-color: #f8fafc;
  border-color: #cbd5e1;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transform: translateY(-1px);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-weight: 500;
  color: #334155;
  background-color: #f8fafc;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

/* Modal styling to match AdminOrders */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: #ffffff;
  border-radius: 10px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
}

.modal-body {
  padding: 20px 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

/* Form styling */
.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #4b5563;
  font-size: 0.95rem;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.95rem;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.checkbox-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.checkbox-group input[type="checkbox"] {
  width: auto;
}

/* Form actions */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.save-btn, .cancel-btn, .delete-btn {
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.save-btn {
  background-color: #3b82f6;
  color: white;
  border: none;
}

.save-btn:hover {
  background-color: #2563eb;
}

.cancel-btn {
  background-color: white;
  color: #4b5563;
  border: 1px solid #d1d5db;
}

.cancel-btn:hover {
  background-color: #f3f4f6;
}

.delete-btn {
  background-color: #ef4444;
  color: white;
  border: none;
}

.delete-btn:hover {
  background-color: #dc2626;
}

/* Image upload styling */
.image-upload-container {
  margin-top: 8px;
}

.image-preview {
  position: relative;
  width: 100%;
  max-width: 300px;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  margin-bottom: 16px;
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #f9fafb;
}

.remove-image-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: rgba(255, 255, 255, 0.9);
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.upload-box {
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  cursor: pointer;
}

.image-input {
  display: none;
}

.upload-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}

.upload-label i {
  font-size: 24px;
  color: #6b7280;
}

/* Loading spinner */
.loading-spinner {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(59, 130, 246, 0.2);
  border-left-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
}

.empty-state i {
  font-size: 48px;
  color: #d1d5db;
  margin-bottom: 16px;
}

.empty-state p {
  color: #6b7280;
  margin-bottom: 24px;
}

/* Full size image modal */
.fullsize-image-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fullsize-image-container {
  position: relative;
  max-width: 90%;
  max-height: 90%;
}

.fullsize-image-container img {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
}

.close-fullimage-btn {
  position: absolute;
  top: -16px;
  right: -16px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: white;
  border: none;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .main-content {
    padding: 12px;
  }
  
  .content-shifted {
    margin-left: 0; /* Remove sidebar space on mobile */
  }
  
  .addon-products-container {
    width: 100%;
    overflow-x: hidden;
  }
  
  .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .header h1 {
    font-size: 1.5rem;
  }
  
  .filter-section, .filter-group {
    flex-direction: column;
  }
  
  .search-box {
    max-width: 100%;
  }
  
  .card {
    border-radius: 8px;
    margin: 0;
    max-width: 100%;
  }
  
  .table-responsive {
    border-radius: 0;
    max-width: 100%;
  }
  
  .addon-products-table {
    min-width: 800px; /* Force horizontal scroll on mobile */
    font-size: 0.875rem;
  }
  
  .addon-products-table th,
  .addon-products-table td {
    padding: 10px 12px;
    white-space: nowrap;
  }
  
  .actions {
    flex-wrap: nowrap;
    gap: 4px;
  }
  
  .action-btn {
    width: 28px;
    height: 28px;
    font-size: 0.75rem;
  }
}

/* Tablet adjustments */
@media (min-width: 769px) and (max-width: 1024px) {
  .main-content {
    padding: 20px;
  }
  
  .addon-products-table {
    font-size: 0.875rem;
  }
  
  .addon-products-table th,
  .addon-products-table td {
    padding: 12px 14px;
  }
}

.warning {
  background-color: #fff7ed;
  color: #9a3412;
  border: 1px solid #fed7aa;
  border-radius: 6px;
  padding: 12px 16px;
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.warning i {
  color: #ea580c;
}

.required {
  color: #ef4444;
  margin-left: 2px;
}

/* Multiple images upload styles */
.multiple-image-upload {
  margin-top: 8px;
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.image-item {
  position: relative;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: #f8fafc;
}

.image-item img {
  width: 100%;
  height: 120px;
  object-fit: cover;
}

.image-controls {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  gap: 4px;
}

.primary-btn, .remove-btn {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
}

.primary-btn {
  background: rgba(255, 255, 255, 0.9);
  color: #fbbf24;
}

.primary-btn.active {
  background: #fbbf24;
  color: white;
}

.remove-btn {
  background: rgba(255, 255, 255, 0.9);
  color: #ef4444;
}

.remove-btn:hover {
  background: #ef4444;
  color: white;
}

.image-order {
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.image-order input {
  width: 40px;
  padding: 2px 4px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
}

/* Product detail modal */
.product-detail-modal {
  max-width: 800px;
}

.product-detail-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.main-image img {
  width: 100%;
  height: 300px;
  object-fit: cover;
  border-radius: 8px;
}

.image-thumbnails {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  overflow-x: auto;
}

.thumbnail {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  border: 2px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s;
}

.thumbnail.active {
  border-color: #3b82f6;
}

.thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-info h3 {
  margin: 0 0 12px 0;
  font-size: 1.5rem;
  color: #1f2937;
}

.product-info .price {
  font-size: 1.25rem;
  font-weight: 600;
  color: #059669;
  margin-bottom: 16px;
}

.product-info .description {
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 20px;
}

.product-meta p {
  margin: 8px 0;
  color: #374151;
}

/* Stock-related styles */
.stock-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.stock-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 60px;
  text-align: center;
}

.stock-badge.good-stock {
  background-color: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.stock-badge.medium-stock {
  background-color: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
}

.stock-badge.low-stock {
  background-color: #fed7d7;
  color: #c53030;
  border: 1px solid #feb2b2;
}

.stock-badge.out-of-stock {
  background-color: #f7fafc;
  color: #718096;
  border: 1px solid #e2e8f0;
}

.low-stock-warning {
  color: #e53e3e;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  gap: 2px;
}

.stock-btn:hover {
  background-color: #f0f9ff;
  color: #0369a1;
}

/* Stock modal styles */
.current-stock-info {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 24px;
  padding: 16px;
  background-color: #f8fafc;
  border-radius: 8px;
}

.stock-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stock-item label {
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 4px;
}

.stock-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
}

.stock-preview {
  margin: 16px 0;
  padding: 12px;
  background-color: #eff6ff;
  border-radius: 6px;
  border-left: 4px solid #3b82f6;
}

.preview-result {
  font-weight: 600;
  color: #1e40af;
  font-size: 1.1rem;
}

.stock-history {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.stock-history h4 {
  margin-bottom: 12px;
  color: #374151;
}

.history-list {
  max-height: 200px;
  overflow-y: auto;
}

.history-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
}

.history-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.history-change {
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  min-width: 40px;
  text-align: center;
}

.history-change.positive {
  background-color: #dcfce7;
  color: #166534;
}

.history-change.negative {
  background-color: #fed7d7;
  color: #c53030;
}

.history-reason {
  color: #4b5563;
  font-size: 0.875rem;
}

.history-meta {
  color: #9ca3af;
  font-size: 0.75rem;
}
</style>
